import React from 'react';
import { BoxProps, Flex, Box } from '@withjoy/joykit';
import { NavLink, NavLinkProps } from '@react-router';
import {
  StyledCategoryMenu,
  StyledDepartmentNavLink,
  StyledDepartmentTitle,
  StyledMegaMenu,
  StyledMegaMenuContainer,
  StyledMenuPartition,
  StyledNavLink,
  StyledNavRoot,
  StyledPartitionCategory,
  StyledPartitionEntry,
  StyledPrimaryNav,
  StyledPrimaryNavItem,
  StyledSubCategory,
  styles,
} from './MegaNav.styles';
import { DepartmentPartitions, useMegaNav } from './useMegaNav';
import { SkeletonGroup, SkeletonText } from '@shared/components/Skeleton';
import { range } from 'lodash-es';
import { NoSsr } from '@shared/components/NoSsr';
import classNames from 'classnames';
import { dataAttr } from '@shared/utils/dom';
import { wrapWithMegaNavHealthProvider } from '@apps/registry/common/components/Catalog/components/MegaNav/MegaNav.health';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useEventType } from '@shared/utils/eventType';
import { EventType } from '@graphql/generated';
import { BrandCard } from '@apps/registry/admin/components/BrandCard/BrandCard';
import { pxToRem } from '@withjoy/joykit/theme';

const MegaNavLink = (props: BoxProps & NavLinkProps & { $isDept: boolean }) => {
  return (
    <StyledNavLink
      display="inline-block"
      _hover={{ color: 'linkHover' }}
      _active={{ color: 'linkActive' }}
      transitionProperties={['color']}
      {...props}
    />
  );
};

const DepartmentNavLink = (props: BoxProps & NavLinkProps & { $isDept: boolean }) => {
  return (
    <StyledDepartmentNavLink
      as={NavLink}
      __css={styles.departmentNavLink}
      {...props}
    />
  );
};

const SubCategoryTitle = ({ children, onClick, to }: { children: React.ReactNode; to: string; onClick?: React.MouseEventHandler }) => {
  return (
    <MegaNavLink
      typographyVariant="body1"
      $isDept={false}
      marginBottom={3}
      to={to}
      onClick={onClick}
    >
      {children}
    </MegaNavLink>
  );
};

const PrimaryNav = (props: BoxProps) => {
  return (
    <StyledPrimaryNav
      __css={styles.primaryNav}
      {...props}
    />
  );
};

const SKELETON_RANGE = range(0, 8);

export const MegaNav = wrapWithMegaNavHealthProvider(() => {
  const {
    activeDepartmentIndex,
    handleOnCategoryClick,
    handleOnMouseLeave,
    loadingDepartments,
    collectMegaMenuRef,
    getDepartmentRoute,
    getIsDeptActive,
    getIsNavItemActive,
    getToCategoryPath,
    partitionedDepartments,
    getToCurationDetailsPath,
    navHeight,
    handleOnMouseOver,
  } = useMegaNav();

  const eventType = useEventType();

  const isBabyDepartmentsScrollable = useResponsive({ values: { '0rem': true, '93rem': false } }, eventType.loading)[0];
  const isWeddingDepartmentsScrollable = useResponsive({ values: { '0rem': true, '85rem': false } }, eventType.loading)[0];

  const isDepartmentsScrollable = eventType.eventType === EventType.wedding ? isWeddingDepartmentsScrollable : isBabyDepartmentsScrollable;

  return (
    <SkeletonGroup
      isReady={!loadingDepartments}
      position="relative"
      transform="translateZ(0)"
      overflow={isDepartmentsScrollable ? 'hidden' : 'visible'}
      width={'100%'}
      overrides={{
        Root: {
          component: StyledNavRoot,
          props: {
            as: 'nav',
            role: 'navigation',
            className: 'meganav',
            display: 'flex',
            justifyContent: 'center',
            zIndex: '4',
          },
        },
      }}
      placeholder={
        <PrimaryNav
          display="inline-flex"
          justifyContent="center"
          paddingX={6}
          width="100%"
        >
          {SKELETON_RANGE.map((_, idx) => (
            <Flex
              key={idx}
              flexDirection="column"
              alignItems="center"
              height="73px"
              marginX={3}
            >
              <Box
                paddingTop={[pxToRem(8), null, pxToRem(12)]}
                paddingBottom={[pxToRem(4), null, pxToRem(12)]}
              >
                <SkeletonText
                  display="inline-flex"
                  alignItems="center"
                  width={'80px'}
                  skeletonHeight={16}
                  rows={1}
                />
              </Box>
            </Flex>
          ))}
        </PrimaryNav>
      }
    >
      <PrimaryNav
        overflowY={'visible'}
        overflowX={isDepartmentsScrollable ? 'scroll' : 'visible'}
        onMouseLeave={handleOnMouseLeave}
        className={classNames({ nav__expanded: activeDepartmentIndex !== -1 })}
        justifyContent={isDepartmentsScrollable ? 'flex-start' : 'center'}
      >
        {partitionedDepartments.map(([dep, partitions, selectedBrandPromo], depIndex) => {
          const lastPartitionWithContent: number = partitions.findIndex(x => x.length === 0);
          const isExpanded = activeDepartmentIndex === depIndex;

          const cNames = classNames({
            'nav-item__expanded': isExpanded,
            'nav-item__active': getIsDeptActive(dep),
          });

          return (
            <StyledPrimaryNavItem
              key={dep.id}
              className={cNames}
              data-expanded={dataAttr(isExpanded)}
              $isExpanded={isExpanded}
              __css={styles.primaryNavItem}
            >
              <DepartmentNavLink
                $isDept={true}
                onMouseOver={() => handleOnMouseOver(dep.id, depIndex)}
                isActive={match => {
                  return getIsNavItemActive(match, dep);
                }}
                to={getDepartmentRoute(dep)}
                onClick={() => {
                  handleOnCategoryClick(dep);
                }}
              >
                <StyledDepartmentTitle
                  as="span"
                  __css={styles.departmentTitle}
                >
                  {dep.title}
                </StyledDepartmentTitle>
              </DepartmentNavLink>
              <NoSsr>
                <StyledMegaMenuContainer
                  as="div"
                  style={{
                    height: `${navHeight}px `,
                    zIndex: isExpanded ? 4 : 0,
                    maxHeight: `calc(90vh - ${pxToRem(192)})`,
                    overflowY: 'auto',
                  }}
                >
                  <StyledMegaMenu
                    as="ul"
                    ref={(e: HTMLUListElement | null) => {
                      collectMegaMenuRef(e, depIndex);
                    }}
                    gridTemplateColumns={`repeat(${4}, 1fr)`}
                    width={'100%'}
                    paddingX={7}
                    marginX="auto"
                  >
                    {partitions.map((items: DepartmentPartitions[number], partitionIndex) => {
                      const shouldShowVerticalDivider = !!items.length && (lastPartitionWithContent !== -1 ? partitionIndex < lastPartitionWithContent - 1 : true);

                      return (
                        <StyledMenuPartition
                          key={partitionIndex}
                          $hasContent={shouldShowVerticalDivider}
                        >
                          <StyledPartitionEntry>
                            {dep.__IS_CURATIONS__ ? (
                              <StyledPartitionCategory>
                                <StyledCategoryMenu>
                                  {items.map(curation =>
                                    curation ? (
                                      <StyledSubCategory key={curation.id}>
                                        <SubCategoryTitle
                                          to={getToCurationDetailsPath(curation.publicUrlPath)}
                                          onClick={() => {
                                            handleOnCategoryClick(curation);
                                          }}
                                        >
                                          {curation.title}
                                        </SubCategoryTitle>
                                      </StyledSubCategory>
                                    ) : null,
                                  )}
                                </StyledCategoryMenu>
                              </StyledPartitionCategory>
                            ) : (
                              items.map(cat => {
                                return cat ? (
                                  <StyledPartitionCategory key={cat.id}>
                                    <MegaNavLink
                                      typographyVariant={dep.__IS_POPULAR_STORES__ ? 'body1' : 'hed1'}
                                      $isDept={false}
                                      marginBottom={4}
                                      to={getToCategoryPath(cat.publicUrlPath)}
                                      onClick={() => {
                                        handleOnCategoryClick(cat);
                                      }}
                                    >
                                      {cat.title}
                                    </MegaNavLink>
                                    {!!cat.children.length && (
                                      <StyledCategoryMenu>
                                        {cat.children.map(subCat =>
                                          dep.__IS_POPULAR_STORES__ ? null : (
                                            <StyledSubCategory key={subCat.id}>
                                              <SubCategoryTitle
                                                to={getToCategoryPath(subCat.publicUrlPath)}
                                                onClick={() => {
                                                  handleOnCategoryClick(subCat);
                                                }}
                                              >
                                                {subCat.title}
                                              </SubCategoryTitle>
                                            </StyledSubCategory>
                                          ),
                                        )}
                                      </StyledCategoryMenu>
                                    )}
                                  </StyledPartitionCategory>
                                ) : null;
                              })
                            )}
                          </StyledPartitionEntry>
                        </StyledMenuPartition>
                      );
                    })}
                    {partitions?.length === 3 && (
                      <StyledMenuPartition $hasContent={true}>
                        <Box
                          width={pxToRem(267)}
                          height={pxToRem(477)}
                          marginX="auto"
                        >
                          <BrandCard
                            brandPromo={selectedBrandPromo}
                            sponsoredTextProps={{
                              color: 'mono10',
                              textAlign: 'left',
                            }}
                            brandCardType="navbar"
                          />
                        </Box>
                      </StyledMenuPartition>
                    )}
                  </StyledMegaMenu>
                </StyledMegaMenuContainer>
              </NoSsr>
            </StyledPrimaryNavItem>
          );
        })}
      </PrimaryNav>
    </SkeletonGroup>
  );
});
